import "./expenseOverviewTotal.css";
import React, { useEffect, useState, useRef } from "react";
import { useSelector } from 'react-redux';
import Clock from "../../images/icons/clock.svg";
import Billable from "../../images/icons/billable.svg";
import NonBillable from "../../images/icons/non-billable.svg";
import PurseMmoney from "../../images/icons/purses-money.svg";
import NotepadIcon from "../../images/icons/notepad.svg";
import { useTotalBudgetOfExpense } from "../../features/expenseData/useExpenseData";

const ExpenseOverviewTotal = (props) => {
  const [totalAmountSpent, setTotalAmountSpent] = useState('0.00');
  const [totalNoExpense, setTotalNoExpense] = useState("0");

  const { forex } = useSelector((state) => state.forex);
  const settings = JSON.parse(localStorage.getItem("settings"));
  const token = useSelector(
    (state) => state.auth.token
  )
  const { data: totalBudget = [], isLoading: totalBudgetLoading, refetch: refetchTotalBudget, isError } = useTotalBudgetOfExpense(props.expCategories, token, forex, props.selectedCurrency);

  // Gradual number animation states
  const [displayedAmountSpent, setDisplayedAmountSpent] = useState(0);
  const [displayedNoExpense, setDisplayedNoExpense] = useState(0);
  const [displayedBudget, setDisplayedBudget] = useState(0);

  // Animation refs
  const amountSpentAnimRef = useRef(null);
  const noExpenseAnimRef = useRef(null);
  const budgetAnimRef = useRef(null);
  const displayedAmountSpentRef = useRef(0);
  const displayedNoExpenseRef = useRef(0);
  const displayedBudgetRef = useRef(0);

  // Use refs to track previous values and prevent infinite loops
  const previousExpenseDataRef = useRef(null);
  const previousSelectedCurrencyRef = useRef(null);
  const previousAllCurrencyRef = useRef(null);
  const previousForexRef = useRef(null);
  const previousCurrencyTotalsRef = useRef(props.currencyTotals);

  useEffect(() => {
    // Compare arrays/objects to check if content has actually changed
    const arraysEqual = (a, b) => {
      if (!a || !b) return a === b;
      if (!Array.isArray(a) || !Array.isArray(b)) return a === b;
      if (a.length !== b.length) return false;
      return a.every((val, idx) => {
        if (val && typeof val === 'object' && b[idx] && typeof b[idx] === 'object') {
          return JSON.stringify(val) === JSON.stringify(b[idx]);
        }
        return val === b[idx];
      });
    };

    const expenseDataChanged = !arraysEqual(previousExpenseDataRef.current, props.expenseData);
    const selectedCurrencyChanged = previousSelectedCurrencyRef.current !== props.selectedCurrency;
    const allCurrencyChanged = previousAllCurrencyRef.current !== props.allCurrency;
    const forexChanged = previousForexRef.current !== forex;
    const currencyTotalsChanged = !arraysEqual(previousCurrencyTotalsRef.current, props.currencyTotals);

    // Only update if something actually changed
    if (expenseDataChanged || selectedCurrencyChanged || allCurrencyChanged || forexChanged || currencyTotalsChanged) {
      const setAmountSpent = () => {
        let totalExpAmount = 0;
        const totals = props.currencyTotals || [];

        if (props.allCurrency === true) {
          // Sum all currencies across all pages
          for (let i = 0; i < totals.length; i++) {
            totalExpAmount += ConvertHelper(totals[i]._id, props.selectedCurrency ? props.selectedCurrency : settings.currency, totals[i].total, forex);
          }
        } else {
          // Only sum the selected currency across all pages
          const matchingCurrency = totals.find(item => item._id === props.selectedCurrency);
          if (matchingCurrency) {
            totalExpAmount = matchingCurrency.total;
          }
        }
        
        totalExpAmount = parseFloat(totalExpAmount).toFixed(2);
        setTotalAmountSpent(totalExpAmount);
      }

      setAmountSpent();

      const setTotalExpenses = () => {
        // Total Expenses count is always the overall totalCount matching the filters
        setTotalNoExpense(props.totalCount || 0);
      }
      setTotalExpenses();

      // Only refetch budget if currency-related props changed, not on every expenseData update
      if (selectedCurrencyChanged || allCurrencyChanged || forexChanged) {
        refetchTotalBudget();
      }

      // Update refs
      previousExpenseDataRef.current = props.expenseData ? [...props.expenseData] : props.expenseData;
      previousSelectedCurrencyRef.current = props.selectedCurrency;
      previousAllCurrencyRef.current = props.allCurrency;
      previousForexRef.current = forex;
    }
  }, [props.expenseData, props.totalCount, props.selectedCurrency, props.allCurrency, forex, refetchTotalBudget]);

  function ConvertHelper(currencyFrom, currencyTo, amount, forex) {
    const currencyFromRate = forex[currencyFrom];
    const currencyToRate = forex[currencyTo];
    const amountConvertedUSD = amount / currencyFromRate;
    const amountConvertTo = amountConvertedUSD * currencyToRate;
    return amountConvertTo;
  }

  // Animation function
  const animateValue = (start, end, setter, animRef, valueRef) => {
    if (animRef.current) {
      cancelAnimationFrame(animRef.current);
    }
    const duration = 1000; // 1 second
    const startTime = performance.now();

    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      const value = start + (end - start) * eased;
      setter(value);
      if (valueRef) {
        valueRef.current = value;
      }
      if (progress < 1) {
        animRef.current = requestAnimationFrame(step);
      } else {
        setter(end);
        if (valueRef) {
          valueRef.current = end;
        }
        animRef.current = null;
      }
    };

    if (Math.abs(start - end) > 0.01) {
      animRef.current = requestAnimationFrame(step);
    } else {
      setter(end);
      if (valueRef) {
        valueRef.current = end;
      }
    }
  };

  // Update refs when displayed values change
  useEffect(() => {
    displayedAmountSpentRef.current = displayedAmountSpent;
  }, [displayedAmountSpent]);

  useEffect(() => {
    displayedNoExpenseRef.current = displayedNoExpense;
  }, [displayedNoExpense]);

  useEffect(() => {
    displayedBudgetRef.current = displayedBudget;
  }, [displayedBudget]);

  // Animate total amount spent
  useEffect(() => {
    if (!props.isLoading && totalAmountSpent !== '0.00') {
      const targetValue = parseFloat(totalAmountSpent);
      const startValue = displayedAmountSpentRef.current;
      animateValue(startValue, targetValue, setDisplayedAmountSpent, amountSpentAnimRef, displayedAmountSpentRef);
    } else if (props.isLoading) {
      setDisplayedAmountSpent(0);
      displayedAmountSpentRef.current = 0;
    }
    return () => {
      if (amountSpentAnimRef.current) {
        cancelAnimationFrame(amountSpentAnimRef.current);
      }
    };
  }, [totalAmountSpent, props.isLoading]);

  // Animate total expenses count
  useEffect(() => {
    if (!props.isLoading && totalNoExpense !== "0") {
      const targetValue = parseInt(totalNoExpense);
      const startValue = displayedNoExpenseRef.current;
      animateValue(startValue, targetValue, setDisplayedNoExpense, noExpenseAnimRef, displayedNoExpenseRef);
    } else if (props.isLoading) {
      setDisplayedNoExpense(0);
      displayedNoExpenseRef.current = 0;
    }
    return () => {
      if (noExpenseAnimRef.current) {
        cancelAnimationFrame(noExpenseAnimRef.current);
      }
    };
  }, [totalNoExpense, props.isLoading]);

  // Animate total budget
  useEffect(() => {
    if (!props.isLoading && !totalBudgetLoading && totalBudget) {
      const targetValue = parseFloat(totalBudget);
      const startValue = displayedBudgetRef.current;
      animateValue(startValue, targetValue, setDisplayedBudget, budgetAnimRef, displayedBudgetRef);
    } else if (props.isLoading || totalBudgetLoading) {
      setDisplayedBudget(0);
      displayedBudgetRef.current = 0;
    }
    return () => {
      if (budgetAnimRef.current) {
        cancelAnimationFrame(budgetAnimRef.current);
      }
    };
  }, [totalBudget, props.isLoading, totalBudgetLoading]);

  return (
    <>
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
      <div className="total-overview-outer">
        <div className="inner-overview-hours">
          <div className="overview-item">
            <div className="overview-title-outer">
              <div className="overview-title">Total Spend</div>
              <div className="overview-icon">
                <img src={Billable} alt="Billable" />
              </div>
            </div>
            <div className="overview-rice">
              <time dateTime="PT1800H">
                {props.isLoading === true ? (
                  <div
                    style={{
                      width: "180px",
                      height: "40px",
                      backgroundColor: "#e5e7eb",
                      borderRadius: "4px",
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  />
                ) : (
                  new Intl.NumberFormat('en-US', { style: 'currency', currency: props.selectedCurrency ? props.selectedCurrency : settings.currency }).format(displayedAmountSpent)
                )}
              </time>
            </div>
          </div>
          <div className="overview-item">
            <div className="overview-title-outer">
              <div className="overview-title">Total Expenses</div>
              <div className="overview-icon">
                <img src={NotepadIcon} alt="notepad-iccon" />
              </div>
            </div>
            <div className="overview-rice">
              <time dateTime="PT1640H">
                {props.isLoading === true ? (
                  <div
                    style={{
                      width: "60px",
                      height: "40px",
                      backgroundColor: "#e5e7eb",
                      borderRadius: "4px",
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  />
                ) : (
                  Math.floor(displayedNoExpense)
                )}
              </time>
            </div>
          </div>
          <div className="overview-item">
            <div className="overview-title-outer">
              <div className="overview-title">Total Budget</div>
              <div className="overview-icon">
                <img src={PurseMmoney} alt="purses-of-money" />
              </div>
            </div>
            <div className="overview-rice">
              <time dateTime="PT160H">
                {(props.isLoading === true || totalBudgetLoading) ? (
                  <div
                    style={{
                      width: "180px",
                      height: "40px",
                      backgroundColor: "#e5e7eb",
                      borderRadius: "4px",
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  />
                ) : (
                  new Intl.NumberFormat('en-US', { style: 'currency', currency: props.selectedCurrency ? props.selectedCurrency : settings.currency }).format(displayedBudget)
                )}
              </time>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExpenseOverviewTotal;